import React, { Component } from 'react';
import lodashGet from 'lodash/get';
import randomColor from 'randomcolor';
import { Loader, Header } from 'semantic-ui-react';
import { connect } from 'react-redux';

import * as actions from '../../../actions';
import Activities from './Activities';
import Info from './Info';
import { SCHOOL_POSTS_LIMIT, SCHOOL_TOPICS_LIMIT } from '../../../constants';
import { HotPosts, Posts } from '../common';
import { TagsChart, TimeSeriesChart, WordCloud } from '../../Charts';
import { TwentyFourHourToTwelveHourFormat } from '../../Utils';
import { selectSchoolInfoResults } from '../../../selectors';

const DEFAULT_HOT_POSTS_SINCE_DAYS_AGO = 1;
const HOT_POSTS_PAGE_SIZE = 20;
const TAGS_TOP_N = 20;
const TOTAL_HOT_POSTS_PAGES = 1;
const TOTAL_SCHOOLS_PAGES = 40;

class School extends Component {
  constructor(props) {
    super(props);
    const { id } = this.props;

    this.state = {
      id,
    };
  }

  componentDidMount() {
    if (this.state.id !== lodashGet(this.props.info, 'id')) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.id !== this.state.id) {
      this.fetchData();
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (props.id !== state.id) {
      return { id: props.id };
    }
    return null;
  }

  fetchMorePosts = activePage => {
    const { getSchoolPosts } = this.props;

    const { id } = this.state;

    const newOffset = (activePage - 1) * SCHOOL_POSTS_LIMIT;
    getSchoolPosts(id, SCHOOL_POSTS_LIMIT, newOffset);
  };

  fetchHotPosts = async (offset, sinceDaysAgo) => {
    await this.props.getSchoolHotPosts(
      this.state.id,
      sinceDaysAgo,
      HOT_POSTS_PAGE_SIZE,
      offset,
    );
  };

  handleHotPostsDaysAgoDropdown = (e, { value }) => {
    this.fetchHotPosts(0, value);
  };

  handleHotPostsPagination = (e, { activePage }) => {
    this.fetchHotPosts(
      (activePage - 1) * HOT_POSTS_PAGE_SIZE,
      DEFAULT_HOT_POSTS_SINCE_DAYS_AGO,
    );
  };

  fetchData = async () => {
    const {
      getSchoolInfo,
      getSchoolInterests,
      getSchoolPosts,
      getSchoolRecentActivities,
      getSchoolTopics,
      hotPosts,
      posts,
    } = this.props;

    const { id } = this.state;

    const process = () =>
      Promise.all([
        getSchoolInfo(id).then(r => {
          if (r.value?.data) {
            this.props.setSeo({
              title: `Dcard | ${r.value.data.name}`,
              description: `Dcard 學校: ${r.value.data.name} 的用戶分析`,
            });
          }
        }),
        getSchoolInterests(id),
        getSchoolTopics(id, SCHOOL_TOPICS_LIMIT),
        getSchoolRecentActivities(id),
        this.fetchHotPosts(
          hotPosts.school === id ? hotPosts.offset : 0,
          hotPosts.school === id
            ? hotPosts.sinceDaysAgo
            : DEFAULT_HOT_POSTS_SINCE_DAYS_AGO,
        ),
        getSchoolPosts(
          id,
          SCHOOL_POSTS_LIMIT,
          posts.school === id ? posts.offset : 0,
        ),
      ]);
    await process();
  };

  render() {
    const {
      authed,
      hotPosts,
      info,
      interests,
      isFetching,
      isMobile,
      posts,
      recentActivities,
      searchTerm,
      topics,
    } = this.props;

    if (isFetching) {
      return <Loader active inline="centered" />;
    }

    if (!info) {
      return <Header as="h1" content="Oops..沒有找到使用者喔" color="grey" />;
    }

    return (
      <div>
        <Info
          authed={authed}
          data={info}
          searchTerm={searchTerm}
          isMobile={isMobile}
        />
        <Activities
          data={recentActivities.data.date}
          isFetching={recentActivities.isFetching}
          isMobile={isMobile}
          title="近期活動 (1週之內)"
        />
        <TimeSeriesChart
          data={recentActivities.data.times.map(({ d, count }) => ({
            indexBy: TwentyFourHourToTwelveHourFormat(d),
            value: count,
          }))}
          keys={['value']}
          indexBy="indexBy"
          isFetching={recentActivities.isFetching}
          isMobile={isMobile}
          color="#4285F4"
          title="活動時間 (1週之內)"
          tooltip={({ value }) => (
            <p>
              發文數 + 評論數: <b>{value}</b>
            </p>
          )}
          enableLabel={false}
        />
        <TagsChart
          title={`興趣取向 - 板 (1週之內) - 前${TAGS_TOP_N}`}
          data={interests.data
            .slice(0, TAGS_TOP_N)
            .map(({ id, name, count }) => ({
              indexBy: name,
              value: count,
              color: randomColor({ seed: id }),
            }))}
          keys={['value']}
          indexBy="indexBy"
          isMobile={isMobile}
          isFetching={interests.isFetching}
          tooltip={({ value, indexValue }) => (
            <p>
              {indexValue}: <b>{value}</b>
            </p>
          )}
        />
        <WordCloud
          data={topics.data}
          getWords={data =>
            data.map(({ topic, count }) => ({ text: topic, value: count }))
          }
          isFetching={topics.isFetching}
          isMobile={isMobile}
          title="發燒話題 (1週之內)"
          height={300}
          width={isMobile ? 300 : 1200}
          fontSizeMapper={word => word.value * (isMobile ? 1.75 : 3.0)}
        />
        <HotPosts
          isMobile={isMobile}
          isFetching={hotPosts.isFetching}
          data={hotPosts.data}
          offset={hotPosts.offset}
          daysAgo={hotPosts.sinceDaysAgo}
          total={TOTAL_HOT_POSTS_PAGES}
          pageSize={HOT_POSTS_PAGE_SIZE}
          onPageChange={this.handleHotPostsPagnation}
          onChangeDaysAgoDropdown={this.handleHotPostsDaysAgoDropdown}
        />
        <Posts
          data={posts.data}
          fetchFunc={this.fetchMorePosts}
          isFetching={posts.isFetching}
          isMobile={isMobile}
          offset={posts.offset}
          pageSize={SCHOOL_POSTS_LIMIT}
          showAuthor
          showForum
          title={`最近${TOTAL_SCHOOLS_PAGES * SCHOOL_POSTS_LIMIT}篇文章`}
          total={TOTAL_SCHOOLS_PAGES}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  hotPosts: state.school.hotPosts,
  info: selectSchoolInfoResults(state),
  interests: state.school.interests,
  isFetching: state.school.info.isFetching,
  isMobile: state.browser.lessThan.medium,
  posts: state.school.posts,
  recentActivities: state.school.recentActivities,
  searchTerm: state.search.q,
  topics: state.school.topics,
});

export default connect(mapStateToProps, actions)(School);
